import { useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button, Flex, For, TextInput } from 'shared';

import { SprintOutcomeItem } from 'features/planning/components/Board/Sprint/Header/Outcomes/SprintOutcomeItem';
import {
  useCreateSprintOutcomeMutation,
  useDeleteSprintOutcomeMutation,
  useEditSprintOutcomeMutation,
} from 'features/planning/hooks/mutation/useSprintOutcomeMutations';
import { SprintOutcome } from 'features/planning/types';

type SprintOutcomesFormValues = {
  sprintOutcomes: SprintOutcome[];
  sprintId: string;
};

type SprintOutcomesProps = {
  sprintId: string;
  userId: string;
};

export const SprintOutcomes = ({ sprintId, userId }: SprintOutcomesProps) => {
  const { control } = useFormContext<SprintOutcomesFormValues>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'sprintOutcomes',
    keyName: 'customId',
  });

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newOutcomeName, setNewOutcomeName] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: createOutcome } = useCreateSprintOutcomeMutation();
  const { mutate: editOutcome } = useEditSprintOutcomeMutation();
  const { mutate: deleteOutcome } = useDeleteSprintOutcomeMutation();

  const handleStartAddNew = () => {
    setIsAddingNew(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewOutcomeName('');
  };

  const handleSaveNew = () => {
    if (!newOutcomeName.trim()) {
      return;
    }

    const newItemIndex = fields.length;

    const tempId = `temp-${Date.now()}`;
    const optimisticOutcome: SprintOutcome = {
      id: tempId,
      name: newOutcomeName.trim(),
      userId: userId,
      status: 'not_started',
      challengeIds: [],
      sprintId,
    };

    append(optimisticOutcome);
    handleCancelAddNew();

    createOutcome(
      {
        name: newOutcomeName.trim(),
        sprintId,
        targetUserId: userId,
      },
      {
        onSuccess: (data: { outcomeId: string }) => {
          update(newItemIndex, {
            ...optimisticOutcome,
            id: data.outcomeId,
          });
        },
        onError: () => {
          console.log('on error');
          const tempIndex = fields.findIndex((field) => field.id === tempId);
          if (tempIndex !== -1) {
            remove(tempIndex);
          }
        },
      }
    );
  };

  const handleStartEditing = (index: number, name: string) => {
    setEditingIndex(index);
    setEditingName(name);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleCancelEditing = () => {
    setEditingIndex(null);
    setEditingName('');
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !editingName.trim()) {
      return;
    }

    const outcome = fields[editingIndex];

    update(editingIndex, { ...outcome, name: editingName.trim() });
    handleCancelEditing();

    editOutcome(
      {
        outcomeId: outcome.id,
        sprintId,
        name: editingName.trim(),
      },
      {
        onError: () => {
          // Revert on error
          update(editingIndex, outcome);
        },
      }
    );
  };

  const handleDelete = (index: number) => {
    const outcome = fields[index];

    remove(index);

    deleteOutcome({
      outcomeId: outcome.id,
      sprintId,
    });
  };

  const handleStatusChange = (
    index: number,
    status: SprintOutcome['status']
  ) => {
    const outcome = fields[index];
    const previousStatus = outcome.status;

    update(index, { ...outcome, status });

    editOutcome(
      {
        outcomeId: outcome.id,
        sprintId,
        status,
      },
      {
        onError: () => {
          update(index, { ...outcome, status: previousStatus });
        },
      }
    );
  };

  const handleKeyDownNew: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveNew();
    } else if (e.key === 'Escape') {
      handleCancelAddNew();
    }
  };

  const handleKeyDownEdit: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEditing();
    }
  };

  return (
    <Flex gap="0.5rem" align="center" flexWrap="wrap">
      <For each={fields}>
        {(outcome, index) => (
          <SprintOutcomeItem
            key={outcome.customId}
            id={outcome.id}
            name={outcome.name}
            status={outcome.status}
            isEditing={editingIndex === index}
            editName={editingIndex === index ? editingName : ''}
            inputRef={inputRef}
            onStartEditing={() => handleStartEditing(index, outcome.name)}
            onCancelEditing={handleCancelEditing}
            onSaveEdit={handleSaveEdit}
            onDelete={() => handleDelete(index)}
            onEditNameChange={setEditingName}
            onKeyDown={handleKeyDownEdit}
            onStatusChange={(status) => handleStatusChange(index, status)}
          />
        )}
      </For>
      {isAddingNew && (
        <Flex gap="0.25rem" align="center">
          <TextInput
            ref={inputRef}
            size="xs"
            value={newOutcomeName}
            placeholder="Outcome name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewOutcomeName(e.target.value)
            }
            onKeyDown={handleKeyDownNew}
            data-testid="add-sprint-outcome-input"
            maxLength={80}
          />
          <Button
            size="xs"
            variant="solid"
            onClick={handleSaveNew}
            data-testid="save-sprint-outcome-button"
            disabled={!newOutcomeName.trim()}
          >
            Save
          </Button>
          <Button
            size="xs"
            variant="solid"
            onClick={handleCancelAddNew}
            data-testid="cancel-sprint-outcome-button"
          >
            Cancel
          </Button>
        </Flex>
      )}
      {!isAddingNew && (
        <Button
          size="xs"
          onClick={handleStartAddNew}
          data-testid="add-sprint-outcome-button"
        >
          + Add outcome
        </Button>
      )}
    </Flex>
  );
};
